/* eslint-disable no-console */

import React, { useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PageContext } from '../../../contexts/ContextCreator';
import BasicDropdown from '../../Input/BasicDropdown/BasicDropdown';
import EmbedlyPreview from '../EmbedlyPreview/EmbedlyPreview';
import useMount from '../../../utilities/hooks/useMount';
import CheckBox from '../../Content/Elements/CheckBox/CheckBox';
import { constructCn } from '../../../utilities/helperFunctions';
// @TODO add hashtagImgSrc import when adding hashtag functionality.
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';

import './ButtonPanel.scss';
import CloudinaryImageUpload from '../../CloudinaryImageUpload/CloudinaryImageUpload';
import PhotoPreview from '../PhotoPreview/PhotoPreview';
import Camera from './Camera';

const cloudinaryWidgetOptions = {
    defaultCoundinaryID: 'icons/user-default',
    uploadPreset: 'community-photo',
    showSkipCropButton: true,
    clientAllowedFormats: ['jpeg', 'png', 'heif', 'webp', 'gif'],
};

const ButtonPanel = ({
    dispatch,
    // insertHashtag,
    isAnonymousPost,
    communityGroupHash,
    disablePhoto,
    pageGroup,
    embedlyUrl,
    previewDisabled,
    image,
}) => {
    const { userFullGroupList = [] } = useContext(PageContext);

    useMount(() => {
        if (pageGroup.hash) {
            dispatch('SELECT_POST_GROUP', { value: pageGroup.hash });
        }
    });

    const canPostToMultipleGroups = useMemo(() => {
        return !!(userFullGroupList.length > 0);
    }, [userFullGroupList.length]);

    const groupOptions = useMemo(() => {
        return [
            { value: '', label: 'Fairygodboss Feed' },
            ...(userFullGroupList || []).map((group) => ({
                value: group.hash,
                label: group.name,
            })),
        ];
    }, [userFullGroupList]);

    const handleToggleAnonymity = useCallback(
        () => dispatch('TOGGLE_POST_ANONYMITY'),
        [dispatch]
    );

    const handleGroupPostingSelection = useCallback(
        ({ target: { value } }) => dispatch('SELECT_POST_GROUP', { value }),
        [dispatch]
    );

    const anonCheckboxClassname = constructCn(
        'post-options-row__section',
        'anon-post-checkbox',
        isAnonymousPost && 'post-anon-bold'
    );

    const handleCloudinaryUpdate = useCallback(
        (cloudinaryId) => {
            dispatch('SET_IMAGE_URL', { value: cloudinaryId });
            dispatch('CLEAR_PHOTO_UPLOAD_ERROR');
            EmitMetric({
                misc_event_type: 'photo-preview-success',
                misc_event_count: 1,
                cloudinary_id: cloudinaryId,
            });
        },
        [dispatch]
    );

    const handleCloudinaryFailure = useCallback(
        (error, result) => {
            if (error && result?.type?.includes('video')) {
                dispatch('SET_PHOTO_UPLOAD_ERROR', {
                    data: {
                        errors: [
                            'Sorry, videos are not supported at this time.',
                        ],
                    },
                });
            }
            const timestamp = new Date();
            EmitMetric({
                misc_event_type: 'photo-preview-failure',
                misc_event_count: 1,
                timestamp,
            });
        },
        [dispatch]
    );

    const handleClickMetric = useCallback(() => {
        EmitMetric({
            misc_event_type: 'photo-upload-button-click',
            misc_event_count: 1,
        });
    }, [dispatch]);

    // @TODO Remove check when hashtag enabled (line 71)
    // @TODO Remove check when Photos enabled (line 81)
    // @TODO Remove check when Photos enabled (line 93)

    return (
        <div className="component-UnifiedPostBox-ButtonPanel">
            <div className="editor-action-buttons">
                {/* TODO: enable after launch */}
                {/* <button
                    className="hashtag-button"
                    type="button"
                    tabIndex={0}
                    onClick={insertHashtag}
                >
                    <img src={hashtagImgSrc} alt="insert-hashtag" />
                </button> */}
                <CloudinaryImageUpload
                    {...cloudinaryWidgetOptions}
                    successCallback={handleCloudinaryUpdate}
                    onUploadFailure={handleCloudinaryFailure}
                    uploadSources={['local']}
                    disabled={disablePhoto}
                >
                    <button
                        className="image-button"
                        type="button"
                        tabIndex={0}
                        disabled={disablePhoto}
                        onClick={handleClickMetric}
                    >
                        <Camera />
                    </button>
                </CloudinaryImageUpload>
            </div>
            {embedlyUrl && !previewDisabled && (
                <EmbedlyPreview url={embedlyUrl} send={dispatch} />
            )}
            {image?.length > 0 && (
                <PhotoPreview
                    image={{ src: image, alt: 'preview-image' }}
                    send={dispatch}
                />
            )}
            <div className="post-options-row">
                <div className={anonCheckboxClassname}>
                    <CheckBox
                        id="unified-post-anonymous"
                        defaultChecked={isAnonymousPost}
                        inputValue={isAnonymousPost}
                        handleChange={handleToggleAnonymity}
                    />
                    Post anonymously
                </div>
                {canPostToMultipleGroups && (
                    <div className="post-options-row__section group-selector">
                        <BasicDropdown
                            cssClass="select--post_to_groups"
                            controlLabel={
                                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                <label htmlFor="post-group-selector">
                                    Post to:
                                </label>
                            }
                            handleRawChangeEvent={handleGroupPostingSelection}
                            selectedValue={communityGroupHash}
                            options={groupOptions}
                            idValue="post-group-selector"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

ButtonPanel.propTypes = {
    dispatch: PropTypes.func.isRequired,
    // insertHashtag: PropTypes.func.isRequired,
    isAnonymousPost: PropTypes.bool.isRequired,
    communityGroupHash: PropTypes.string,
    disablePhoto: PropTypes.bool,
    pageGroup: PropTypes.shape({
        hash: PropTypes.string,
        name: PropTypes.string,
    }),
    embedlyUrl: PropTypes.string,
    previewDisabled: PropTypes.bool.isRequired,
    image: PropTypes.string,
};

ButtonPanel.defaultProps = {
    communityGroupHash: '',
    disablePhoto: false,
    pageGroup: {},
    embedlyUrl: '',
    image: null,
};

ButtonPanel.displayName = 'ButtonPanel';

export default ButtonPanel;
